import classNames from 'classnames';
import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import Fade, { FadeProps } from './Fade';
import Portal from './Portal';
import {
  conditionallyUpdateScrollbar,
  focusableElements,
  getOriginalBodyPadding,
  getTarget,
  keyCodes,
  omit,
  setScrollbarWidth,
  TransitionTimeouts
} from './utils';

export interface ModalProps {
  isOpen?: boolean;
  autoFocus?: boolean;
  centered?: boolean;
  scrollable?: boolean;
  size?: string;
  toggle?: (e?: React.SyntheticEvent | KeyboardEvent | MouseEvent) => void;
  keyboard?: boolean;
  role?: string;
  labelledBy?: string;
  backdrop?: boolean | 'static';
  onEnter?: () => void;
  onExit?: () => void;
  onOpened?: () => void;
  onClosed?: () => void;
  children?: React.ReactNode;
  className?: string;
  wrapClassName?: string;
  modalClassName?: string;
  backdropClassName?: string;
  contentClassName?: string;
  external?: React.ReactNode;
  fade?: boolean;
  zIndex?: number | string;
  backdropTransition?: Partial<FadeProps>;
  modalTransition?: Partial<FadeProps>;
  innerRef?: React.Ref<HTMLElement>;
  unmountOnClose?: boolean;
  returnFocusAfterClose?: boolean;
  container?: Element | string | { current: Element | null };
  trapFocus?: boolean;
  style?: React.CSSProperties;
  'data-testid'?: string;
  [key: string]: unknown;
}

const propsToOmit = [
  'isOpen',
  'autoFocus',
  'centered',
  'scrollable',
  'size',
  'toggle',
  'keyboard',
  'role',
  'labelledBy',
  'backdrop',
  'onEnter',
  'onExit',
  'onOpened',
  'onClosed',
  'children',
  'className',
  'wrapClassName',
  'modalClassName',
  'backdropClassName',
  'contentClassName',
  'external',
  'fade',
  'zIndex',
  'backdropTransition',
  'modalTransition',
  'innerRef',
  'unmountOnClose',
  'returnFocusAfterClose',
  'container',
  'trapFocus'
];

let openCount = 0;

const Modal = (props: ModalProps) => {
  const {
    isOpen = false,
    autoFocus = true,
    centered = false,
    scrollable = false,
    size,
    toggle,
    keyboard = true,
    role = 'dialog',
    labelledBy,
    backdrop = true,
    onEnter,
    onExit,
    onOpened,
    onClosed,
    children,
    className,
    wrapClassName,
    modalClassName,
    backdropClassName,
    contentClassName,
    external,
    fade = true,
    zIndex = 1050,
    backdropTransition,
    modalTransition,
    innerRef,
    unmountOnClose = true,
    returnFocusAfterClose = true,
    container = 'body',
    trapFocus = false
  } = props;

  const elementRef = useRef<HTMLDivElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const triggeringElementRef = useRef<Element | null>(null);
  const mountContainerRef = useRef<Element | null>(null);
  const mouseDownElementRef = useRef<EventTarget | null>(null);
  const originalBodyPaddingRef = useRef<number>(0);
  const modalIndexRef = useRef<number>(0);
  const isMountedRef = useRef<boolean>(false);
  const backdropAnimationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [internalOpen, setInternalOpen] = useState(false);
  const [showStaticBackdropAnimation, setShowStaticBackdropAnimation] = useState(false);

  const getFocusableChildren = useCallback(() => {
    return elementRef.current!.querySelectorAll<HTMLElement>(focusableElements.join(', '));
  }, []);

  const getFocusedChild = useCallback(() => {
    const focusableChildren = getFocusableChildren();
    try {
      return document.activeElement as HTMLElement;
    } catch {
      return focusableChildren[0]!;
    }
  }, [getFocusableChildren]);

  const setFocus = useCallback(() => {
    if (
      dialogRef.current &&
      dialogRef.current.parentNode &&
      typeof (dialogRef.current.parentNode as HTMLElement).focus === 'function'
    ) {
      (dialogRef.current.parentNode as HTMLElement).focus();
    }
  }, []);

  const manageFocusAfterClose = useCallback(() => {
    if (triggeringElementRef.current) {
      const elem = triggeringElementRef.current as HTMLElement;
      if (elem.focus && returnFocusAfterClose) elem.focus();
      triggeringElementRef.current = null;
    }
  }, [returnFocusAfterClose]);

  const close = useCallback(() => {
    if (openCount <= 1) {
      const modalOpenClassName = 'modal-open';
      const modalOpenClassNameRegex = new RegExp(`(^| )${modalOpenClassName}( |$)`);
      document.body.className = document.body.className.replace(modalOpenClassNameRegex, ' ').trim();
    }
    manageFocusAfterClose();
    openCount = Math.max(0, openCount - 1);
    setScrollbarWidth(originalBodyPaddingRef.current);
  }, [manageFocusAfterClose]);

  const destroy = useCallback(() => {
    if (elementRef.current && mountContainerRef.current) {
      mountContainerRef.current.removeChild(elementRef.current);
      elementRef.current = null;
    }
    manageFocusAfterClose();
  }, [manageFocusAfterClose]);

  const init = useCallback(() => {
    try {
      triggeringElementRef.current = document.activeElement;
    } catch {
      triggeringElementRef.current = null;
    }

    if (!elementRef.current) {
      elementRef.current = document.createElement('div');
      elementRef.current.setAttribute('tabindex', '-1');
      elementRef.current.style.position = 'relative';
      elementRef.current.style.zIndex = String(zIndex);
      mountContainerRef.current = getTarget(container);
      mountContainerRef.current!.appendChild(elementRef.current);
    }

    originalBodyPaddingRef.current = getOriginalBodyPadding();
    conditionallyUpdateScrollbar();

    if (openCount === 0) {
      document.body.className = classNames(document.body.className, 'modal-open');
    }

    modalIndexRef.current = openCount;
    openCount += 1;
  }, [container, zIndex]);

  const handleStaticBackdropAnimation = useCallback(() => {
    if (backdropAnimationTimeoutRef.current) {
      clearTimeout(backdropAnimationTimeoutRef.current);
      backdropAnimationTimeoutRef.current = null;
    }
    setShowStaticBackdropAnimation(true);
    backdropAnimationTimeoutRef.current = setTimeout(() => setShowStaticBackdropAnimation(false), 100);
  }, []);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === mouseDownElementRef.current) {
        e.stopPropagation();
        const backdropEl = dialogRef.current ? dialogRef.current.parentNode : null;

        if (backdropEl && e.target === backdropEl && backdrop === 'static') {
          handleStaticBackdropAnimation();
        }

        if (!isOpen || backdrop !== true) return;

        if (backdropEl && e.target === backdropEl && toggle) {
          toggle(e);
        }
      }
    },
    [backdrop, isOpen, toggle, handleStaticBackdropAnimation]
  );

  const handleBackdropMouseDown = useCallback((e: React.MouseEvent) => {
    mouseDownElementRef.current = e.target;
  }, []);

  const handleEscape = useCallback(
    (e: React.KeyboardEvent) => {
      if (isOpen && e.keyCode === keyCodes.esc && toggle) {
        if (keyboard) {
          e.preventDefault();
          e.stopPropagation();
          toggle(e);
        } else if (backdrop === 'static') {
          e.preventDefault();
          e.stopPropagation();
          handleStaticBackdropAnimation();
        }
      }
    },
    [isOpen, toggle, keyboard, backdrop, handleStaticBackdropAnimation]
  );

  const handleTab = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.which !== 9) return;
      if (modalIndexRef.current < openCount - 1) return;

      const focusableChildren = getFocusableChildren();
      const totalFocusable = focusableChildren.length;
      if (totalFocusable === 0) return;
      const currentFocus = getFocusedChild();

      let focusedIndex = 0;
      for (let i = 0; i < totalFocusable; i += 1) {
        if (focusableChildren[i] === currentFocus) {
          focusedIndex = i;
          break;
        }
      }

      if (e.shiftKey && focusedIndex === 0) {
        e.preventDefault();
        focusableChildren[totalFocusable - 1]!.focus();
      } else if (!e.shiftKey && focusedIndex === totalFocusable - 1) {
        e.preventDefault();
        focusableChildren[0]!.focus();
      }
    },
    [getFocusableChildren, getFocusedChild]
  );

  const trapFocusHandler = useCallback(
    (ev: FocusEvent) => {
      if (!trapFocus) return;
      if (!elementRef.current) return;
      if (dialogRef.current && dialogRef.current.parentNode === ev.target) return;
      if (modalIndexRef.current < openCount - 1) return;

      const childrenList = getFocusableChildren();
      for (let i = 0; i < childrenList.length; i++) {
        if (childrenList[i] === ev.target) return;
      }
      if (childrenList.length > 0) {
        ev.preventDefault();
        ev.stopPropagation();
        childrenList[0]!.focus();
      }
    },
    [trapFocus, getFocusableChildren]
  );

  // mount / unmount lifecycle
  useEffect(() => {
    if (isOpen) {
      init();
      setInternalOpen(true);
      if (autoFocus) {
        // focus after dialog is rendered
        setTimeout(() => setFocus(), 0);
      }
    }
    if (onEnter) onEnter();

    document.addEventListener('focus', trapFocusHandler, true);
    isMountedRef.current = true;

    return () => {
      if (backdropAnimationTimeoutRef.current) {
        clearTimeout(backdropAnimationTimeoutRef.current);
        backdropAnimationTimeoutRef.current = null;
      }
      if (onExit) onExit();
      if (elementRef.current) {
        destroy();
        if (isOpen || internalOpen) {
          close();
        }
      }
      document.removeEventListener('focus', trapFocusHandler, true);
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // open/close updates
  const prevIsOpenRef = useRef<boolean>(isOpen);
  const prevInternalOpenRef = useRef<boolean>(internalOpen);
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      init();
      setInternalOpen(true);
    }
    if (autoFocus && internalOpen && !prevInternalOpenRef.current) {
      setFocus();
    }
    if (elementRef.current) {
      elementRef.current.style.zIndex = String(zIndex);
    }
    prevIsOpenRef.current = isOpen;
    prevInternalOpenRef.current = internalOpen;
  }, [isOpen, internalOpen, autoFocus, init, setFocus, zIndex]);

  useImperativeHandle(innerRef, () => elementRef.current as HTMLElement, []);

  const handleOnOpened = useCallback(() => {
    onOpened?.();
    (modalTransition?.onEntered as ((...args: unknown[]) => void) | undefined)?.();
  }, [onOpened, modalTransition]);

  const handleOnClosed = useCallback(() => {
    onClosed?.();
    (modalTransition?.onExited as ((...args: unknown[]) => void) | undefined)?.();
    if (unmountOnClose) {
      destroy();
    }
    close();
    if (isMountedRef.current) {
      setInternalOpen(false);
    }
  }, [onClosed, modalTransition, unmountOnClose, destroy, close]);

  if (!elementRef.current || !(internalOpen || !unmountOnClose)) {
    return null;
  }

  const isModalHidden = !internalOpen && !unmountOnClose;
  elementRef.current.style.display = isModalHidden ? 'none' : 'block';

  const modalAttributes = {
    onClick: handleBackdropClick,
    onMouseDown: handleBackdropMouseDown,
    onKeyUp: handleEscape,
    onKeyDown: handleTab,
    style: { display: 'block' as const },
    'aria-labelledby': labelledBy,
    role,
    tabIndex: -1
  };

  const hasTransition = fade;
  const modalTransitionProps = {
    ...modalTransition,
    baseClass: hasTransition ? modalTransition?.baseClass ?? 'fade' : '',
    timeout: hasTransition ? modalTransition?.timeout ?? TransitionTimeouts.Modal : 0
  };
  const backdropTransitionProps = {
    mountOnEnter: true,
    ...backdropTransition,
    baseClass: hasTransition ? backdropTransition?.baseClass ?? 'fade' : '',
    timeout: hasTransition ? backdropTransition?.timeout ?? TransitionTimeouts.Fade : 0
  };

  const dialogBaseClass = 'modal-dialog';
  const dialogClasses = classNames(dialogBaseClass, className, {
    [`modal-${size}`]: size,
    [`${dialogBaseClass}-centered`]: centered,
    [`${dialogBaseClass}-scrollable`]: scrollable
  });

  const dialogAttributes = omit(props, propsToOmit);

  const Backdrop =
    backdrop &&
    (hasTransition ? (
      <Fade
        {...(backdropTransitionProps as Partial<FadeProps>)}
        in={isOpen && !!backdrop}
        className={classNames('modal-backdrop', backdropClassName)}
      />
    ) : (
      <div className={classNames('modal-backdrop', 'show', backdropClassName)} />
    ));

  return (
    <Portal node={elementRef.current}>
      <div className={wrapClassName}>
        <Fade
          {...modalAttributes}
          {...(modalTransitionProps as Partial<FadeProps>)}
          in={internalOpen}
          onEntered={handleOnOpened}
          onExited={handleOnClosed}
          className={classNames(
            'modal',
            modalClassName,
            showStaticBackdropAnimation && 'modal-static'
          )}
        >
          {external}
          <div {...(dialogAttributes as object)} className={dialogClasses} role="document" ref={dialogRef}>
            <div className={classNames('modal-content', contentClassName)}>{children}</div>
          </div>
        </Fade>
        {Backdrop}
      </div>
    </Portal>
  );
};

export default Modal;
